import React, { useState } from 'react';
import cn from 'classnames';
import { noop } from 'lodash';
import { Button, Status, toaster } from '@fiverr-private/fit';
import { I18n, translate } from '@fiverr-private/i18n-react';
import Variation from '../../base/Variation';
import { DRAFT_VARIATION_CARD } from '../../../propTypes';
import CardSellerProfile from '../CardSellerProfile';
import { DuplicateIcon, DeleteIcon, DuplicateIconInProgress } from './assets';
import {
    CUSTOMIZE_AND_BUY_PANNEL,
    DELETE_PANNEL,
    CUSTOMIZE_LINK,
    PROJECT_ID_PARAM,
    VARIATION_ID_PARAM
} from './constants';
import './index.scss';

const getCustomizeLink = (projectId, variationId) => (
    CUSTOMIZE_LINK.replace(PROJECT_ID_PARAM, projectId).replace(VARIATION_ID_PARAM, variationId)
);

const DraftVariationCard = ({
    draftId,
    projectId,
    onDelete = noop,
    onCustomize = noop,
    onDuplicate = noop,
    onBuyAndDownload = noop,
    variation,
    isDeleteDisabled = false,
    isDuplicateDisabled = false,
    seller,
    isFreeVariation = false,
    href
}) => {
    const [displayedPannel, setDisplayedPannel] = useState(CUSTOMIZE_AND_BUY_PANNEL);
    const [isPurchasing, setIsPurchasing] = useState(false);

    const showDeletePannel = () => setDisplayedPannel(DELETE_PANNEL);

    const showCustomizeAndBuyPannel = () => setDisplayedPannel(CUSTOMIZE_AND_BUY_PANNEL);

    const handlePurchase = async() => {
        setIsPurchasing(true);

        try {
            await onBuyAndDownload();
        } catch {
            toaster.raise(
                <Status color={Status.COLORS.RED}>
                    <I18n k="variations_display.cards.draft.error"/>
                </Status>,
                { location: toaster.LOCATIONS.TOP_CENTER }
            );
        }

        setIsPurchasing(false);
    };

    return (
        <div className="draft-variation-card-wrapper">
            <div className={cn('draft-variation-card', {
                'with-delete-panel': displayedPannel === DELETE_PANNEL,
                'in-progress': isDuplicateDisabled
            })}>
                <Variation {...variation} withWatermark={true}/>
                <div className="card-overlay">
                    <div className="card-overlay-ctas">
                        <Button onClick={() => onDuplicate(draftId)}
                            disabled={isDuplicateDisabled}
                            className="duplicate-draft hint--bottom-right"
                            data-hint={translate('variations_display.cards.draft.duplicate')}>
                            { isDuplicateDisabled ? <DuplicateIconInProgress/> : <DuplicateIcon/> }
                        </Button>

                        <Button onClick={showDeletePannel}
                            className="delete-draft hint--bottom-right"
                            data-hint={translate('variations_display.cards.draft.delete')}>
                            <DeleteIcon/>
                        </Button>
                    </div>
                    <div className="card-overlay-content">
                        {displayedPannel === CUSTOMIZE_AND_BUY_PANNEL && (
                            <>
                                <Button href={getCustomizeLink(projectId, variation.id)}
                                    onClick={onCustomize}
                                    className="btn-wide customize-btn"
                                    color="blue">
                                    <I18n k="variations_display.cards.draft.edit"/>
                                </Button>
                                <Button {...href && ({ href })}
                                    onClick={handlePurchase}
                                    disabled={isPurchasing}
                                    loading={isPurchasing}
                                    className="btn-wide"
                                    color="blue"
                                    fill="ghost">
                                    <I18n k={`variations_display.cards.draft.${isFreeVariation ? 'download' : 'buy_and_download'}`}/>
                                </Button>
                            </>
                        )}
                        {displayedPannel === DELETE_PANNEL && (
                            <>
                                <p className="delete-title">
                                    <I18n k="variations_display.cards.draft.delete_title"/>
                                </p>
                                <p className="delete-description">
                                    <I18n k="variations_display.cards.draft.delete_description"/>
                                </p>
                                <div className="delete-pannel-ctas">
                                    <Button onClick={showCustomizeAndBuyPannel}
                                        className="btn-narrow"
                                        color="grey"
                                        fill="ghost">
                                        <I18n k="variations_display.cards.draft.cancel"/>
                                    </Button>
                                    <Button onClick={() => onDelete(draftId)}
                                        disabled={isDeleteDisabled}
                                        className="btn-narrow"
                                        color="red">
                                        <I18n k="variations_display.cards.draft.delete"/>
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {seller.username && <CardSellerProfile {...seller} withSellerLink={true}/>}
        </div>
    );
};

DraftVariationCard.propTypes = DRAFT_VARIATION_CARD;

export default DraftVariationCard;
